import React, { useState, useEffect, useRef } from "react";
import PulseLoader from "@/components/PulseLoader";
import { useLocation } from "react-router-dom";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import authService from "../../../utils/authService";
import ScheduleDisplay from "../../../components/ScheduleDisplay";
import SubjectReorderPanel from "../../../components/SubjectReorderPanel";
import DayTypeInsertModal from "../../../components/DayTypeInsertModal";
import TestTypeSelectModal from "../../../components/TestTypeSelectModal";
import {
  ScheduleData,
  Selection,
  CourseData,
  DayTypeMap,
  TestKind,
  editScheduleData,
  extractLearningOnlySchedule,
  recalculateScheduleForHourChange,
  updateDurationInPlace,
  extractMatchingDays,
  LEGACY_SPECIAL_DAY_TOPICS,
  getLastDateOfPreviousSubject,
  updateScheduleWithCustomDate,
  adjustSubsequentSubjects,
  clearHolidayCache,
  clearLastDateCache,
  editScheduleDataWithDayTypes,
  updateCustomSubjectDatesFromSchedule,
  convertToScheduleData,
  reorderCourseData,
  extractSubjectNamesFromSchedule,
  extractSubjectNamesFromCourseData,
  isSubjectReorderLocked,
  reorderScheduleBySubjectNames,
  filterCourseDataByIds,
  subjectNamesToIds,
  canUnselectSubject,
  filterScheduleBySelectedNames,
  closeScheduleGapsAfterSubject,
  renumberScheduleDays,
  preserveUndatedSubjectDates,
  applyLiveSessionSubtopicIds,
  LIVE_SESSION_TOPIC,
} from "../../../utils/scheduleUtils";
import {
  buildDayTypesFromCourseLivePlans,
  stampLiveSessionsFromCoursePlans,
  applyLcmMarkersFromCoursePlans,
  clearLcmMarkersFromSchedule,
  markDayWithLcm,
  planHasLcmSessions,
  SubjectLivePlanInput,
} from "../../../utils/courseLivePlanDayTypes";

const DayWiseGroup: React.FC = () => {
  const [schedule, setSchedule] = useState<ScheduleData>({});
  const [isLoading, setIsLoading] = useState(false);
  // Separate from isLoading (page fetch); drives confirm-modal save spinner only
  const [isSaving, setIsSaving] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: string }>({});
  const [selectedDurations, setSelectedDurations] = useState<{ [key: string]: string }>({});
  const [fetchedJson, setFetchedJson] = useState<CourseData>({});
  const [showModal, setShowModal] = useState(false);
  const location = useLocation();
  const [modalNewMessage, setModalNewMessage] = useState<string>("");
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const data = location.state?.data as any;
  const [dayType, setDayType] = useState<DayTypeMap>({});
  const [showInsertModal, setShowInsertModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [currentDay, setCurrentDay] = useState('');
  const [currentSubjectForTest, setCurrentSubjectForTest] = useState('');
  const [selectedDayType, setSelectedDayType] = useState('');
  const [editingDateForSubject, setEditingDateForSubject] = useState<string | null>(null);
  const [customSubjectDates, setCustomSubjectDates] = useState<{ [key: string]: string }>({});
  const [maxDay, setMaxDay] = useState<number>(0);
  
  // Use ref to prevent multiple executions
  const hasInitialized = useRef(false);
  const isFetching = useRef(false);
  // User-defined subject order; editScheduleData uses fetchedJson key order on regenerate
  const subjectOrderRef = useRef<string[]>([]);
  // Full course (for panel); selectedNames drive schedule + subject_released
  const fullCourseRef = useRef<CourseData>({});
  const selectedNamesRef = useRef<string[]>([]);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  /** Course live plans used to auto-insert workshops + LCM markers */
  const livePlanInputsRef = useRef<SubjectLivePlanInput[]>([]);
  const [includeLcmFromPlan, setIncludeLcmFromPlan] = useState(true);

  // Re-apply order after hour/day-type regeneration (those rebuild from course data)
  const applySubjectOrder = (newSchedule: ScheduleData): ScheduleData => {
    const order = subjectOrderRef.current;
    const ordered = order.length > 0
      ? reorderScheduleBySubjectNames(newSchedule, order)
      : newSchedule;
    const withLiveIds = applyLiveSessionSubtopicIds(ordered);
    if (!livePlanInputsRef.current.length) return withLiveIds;
    return stampLiveSessionsFromCoursePlans(
      withLiveIds,
      livePlanInputsRef.current,
    );
  };

  const applyPlanWorkshops = (baseSchedule: ScheduleData) => {
    if (!livePlanInputsRef.current.length) return;
    const learningOnly = extractLearningOnlySchedule(baseSchedule);
    const autoDayTypes = buildDayTypesFromCourseLivePlans(
      learningOnly,
      livePlanInputsRef.current,
    );
    if (Object.keys(autoDayTypes).length > 0) {
      setDayType(autoDayTypes);
    }
  };

  const applyOrClearAllLcm = (
    baseSchedule: ScheduleData,
    include: boolean,
  ): ScheduleData => {
    if (!livePlanInputsRef.current.length) return baseSchedule;
    if (!include) return clearLcmMarkersFromSchedule(baseSchedule);
    return applyLcmMarkersFromCoursePlans(
      baseSchedule,
      livePlanInputsRef.current,
    );
  };

  // Helper function to check if a day is restricted
  const isDayRestricted = (dayString: string): boolean => {
    if (maxDay === 0) return false; // No restrictions if maxDay is 0
    
    const dayNumber = parseInt(dayString.split(' ')[1]);
    return dayNumber <= maxDay;
  };

  // Helper function to check if a subject's date change is restricted
  const isSubjectDateRestricted = (subjectName: string): boolean => {
    if (maxDay === 0) return false; // No restrictions if maxDay is 0
    
    const subjectDays = schedule[subjectName];
    if (!Array.isArray(subjectDays) || subjectDays.length === 0) return false;

    // Undated / newly mapped subjects can always set or change start date
    if (subjectDays.every((day: any) => !day.date)) return false;
    
    // Check if any day of this subject is within the restricted range
    return subjectDays.some((day: any) => {
      const dayNumber = parseInt(day.day.split(' ')[1]);
      return dayNumber <= maxDay;
    });
  };

  const selection: Selection = {
    saturdayStudyDay: data?.saturdayStudyDay ?? "",
    startDate: data?.startDate ?? "",
    sundayHoliday: data?.sundayHoliday ?? "",
    course_id: data?.course_id ?? "",
    batch_id: data?.batch_id ?? "",
    hoursPerDay: data?.hoursPerDay,
  };


  // Single useEffect to handle both navigation and initial data loading
  useEffect(() => {

    
    // Only proceed if we have a valid course_id
    if (!selection.course_id) {

      return;
    }
    
    // Prevent multiple executions
    if (hasInitialized.current && !location.pathname) {

      return;
    }
    
    // Prevent concurrent fetches
    if (isFetching.current) {

      return;
    }
    

    isFetching.current = true;
    
    // Reset state only when navigating (location changes)
    if (location.pathname) {

      setSchedule({});
      setFetchedJson({});
      setCustomSubjectDates({});
      setDayType({});
      setSelectedOptions({});
      setSelectedDurations({});
      setEditingDateForSubject(null);
      setShowInsertModal(false);
      setShowModal(false);
      setShowUpdateModal(false);
      livePlanInputsRef.current = [];
      setIncludeLcmFromPlan(true);
    }
    
    // Fetch data only if we don't have it yet
    if (Object.keys(fetchedJson).length === 0) {

      const fetchData = async () => {
        try {
          setIsLoading(true);
          const json = { course_id: selection.course_id };
          const response = await authService.post("admin/get_all_data_of_course/", json);

          
          setFetchedJson(response.data.course_data);
          
          // Fetch max day restriction
          try {
            const maxDayResponse = await authService.get(`admin/batch/max_day/${selection.batch_id}/`);
            setMaxDay(maxDayResponse.data.max_day || 0);
          } catch (error) {
            console.error("Error fetching max day:", error);
            setMaxDay(0);
          }
          
          // Create initial schedule from course data
          const initialSchedule = convertToScheduleData(response.data.course_data, selection);

          
          setSchedule(initialSchedule);

          const loadedOrder = Object.keys(initialSchedule);
          subjectOrderRef.current = loadedOrder;
          // Align course data iteration order with initial schedule before any regenerate
          setFetchedJson(reorderCourseData(response.data.course_data, loadedOrder));
          fullCourseRef.current = response.data.course_data;
          selectedNamesRef.current = loadedOrder;
          setSelectedNames(loadedOrder);
          
          // Initialize customSubjectDates with initial subject start dates
          const subjectStartDates: { [key: string]: string } = {};
          Object.keys(initialSchedule).forEach(subjectName => {
            const subjectDays = initialSchedule[subjectName];
            if (Array.isArray(subjectDays) && subjectDays.length > 0) {
              const sortedDays = [...subjectDays].sort((a: any, b: any) => 
                new Date(a.date).getTime() - new Date(b.date).getTime()
              );
              const firstDate = sortedDays[0].date;
              // Convert Date to string if needed
              const dateString = typeof firstDate === 'string' ? firstDate : new Date(firstDate).toISOString().split('T')[0];
              subjectStartDates[subjectName] = dateString;
            }
          });
          setCustomSubjectDates(subjectStartDates);

          // ---------------------------------------------------------------
          // Auto-insert Live Session days from course live session plans
          // (after last day that covers each LCM session's topics)
          // ---------------------------------------------------------------
          try {
            const courseData = response.data.course_data as CourseData;
            const planInputs: SubjectLivePlanInput[] = [];
            await Promise.all(
              Object.entries(courseData).map(async ([subjectId, subjectData]) => {
                const subjectName =
                  subjectData?.subject_name || subjectId;
                if (!loadedOrder.includes(subjectName)) return;
                try {
                  const planRes = await authService.get(
                    `admin/course_live_session_plan/${selection.course_id}/${subjectId}/`,
                  );
                  const sessions = planRes?.data?.sessions || [];
                  if (!sessions.length) return;
                  planInputs.push({
                    subjectName,
                    sessions,
                    courseTopics: subjectData?.topics || [],
                  });
                } catch (planErr) {
                  console.warn(
                    `No course live session plan for ${subjectId}:`,
                    planErr,
                  );
                }
              }),
            );

            livePlanInputsRef.current = planInputs;
            const hasLcm = planHasLcmSessions(planInputs);
            setIncludeLcmFromPlan(hasLcm);

            let scheduleWithLcm = initialSchedule;
            if (planInputs.length > 0 && hasLcm) {
              scheduleWithLcm = applyLcmMarkersFromCoursePlans(
                initialSchedule,
                planInputs,
              );
              setSchedule(applySubjectOrder(scheduleWithLcm));
            }

            if (planInputs.length > 0) {
              applyPlanWorkshops(scheduleWithLcm);
            }
          } catch (planApplyErr) {
            console.warn(
              'Failed to apply course live session plans to day-wise:',
              planApplyErr,
            );
          }

          hasInitialized.current = true;
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setIsLoading(false);
          isFetching.current = false;
        }
      };
      
      fetchData();
    } else {

      isFetching.current = false;
    }
  }, [location.pathname, selection.course_id]); // Only depend on location and course_id

  // Remove the automatic sequential date enforcement for new schedule creation
  // This useEffect was causing dates to shift forward unnecessarily

  const setSelectedOption = (value: string, day: string) => {
    setSelectedOptions(prevState => ({ ...prevState, [day]: value }));
  };
  
  useEffect(() => {
    if (Object.keys(dayType).length === 0) {
      return;
    }
    
    const timeoutId = setTimeout(() => {
      // Keep specials already on the schedule, then apply dayType updates
      const mergedDayType = {
        ...extractMatchingDays(schedule, [...LEGACY_SPECIAL_DAY_TOPICS]),
        ...dayType,
      };
      const baseSchedule = extractLearningOnlySchedule(schedule);
      const finalSchedule = editScheduleDataWithDayTypes(
        baseSchedule,
        mergedDayType,
        customSubjectDates,
        selection.startDate,
        selection.saturdayStudyDay,
        selection.sundayHoliday
      );
      const ordered = applySubjectOrder(preserveUndatedSubjectDates(schedule, finalSchedule));
      setSchedule(ordered);
      setCustomSubjectDates(updateCustomSubjectDatesFromSchedule(ordered));
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [dayType]);

  // Utility function to validate schedule dates
  const validateScheduleDates = (): boolean => {
    try {
      const subjects = Object.keys(schedule);
      for (const subject of subjects) {
        const days = schedule[subject];
        if (days && days.length > 0) {
          const sortedDays = [...days].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
          for (let i = 1; i < sortedDays.length; i++) {
            const prevDate = new Date(sortedDays[i-1].date);
            const currDate = new Date(sortedDays[i].date);
            if (currDate <= prevDate) {
              return false;
            }
          }
        }
      }
      return true;
    } catch (error) {
      console.error('Error validating schedule dates:', error);
      return false;
    }
  };
  
  const setSelectedDuration = (value: string, day: string) => {
    setSelectedDurations(prevState => ({ ...prevState, [day]: value }));
  };

  const handleTimeClick = (selectedDay: string, hours: string) => {
    if (isDayRestricted(selectedDay)) {
      alert(`Cannot modify hours for Day ${selectedDay.split(' ')[1]} as it is within the restricted range (up to Day ${maxDay})`);
      return;
    }
    // Recalculate content + actualTime using existing schedule (does NOT rebuild from course JSON).
    // Keeps other days intact by repacking from what's already on the screen.
    const finalSchedule = recalculateScheduleForHourChange(
      schedule,
      fetchedJson,
      selection,
      selectedDay,
      Number(hours),
      customSubjectDates
    );

    const orderedSchedule = applySubjectOrder(
      preserveUndatedSubjectDates(schedule, finalSchedule)
    );

    setCustomSubjectDates(updateCustomSubjectDatesFromSchedule(orderedSchedule));
    setSchedule(orderedSchedule);
    // Clear stale dropdown selections after repacking/renumbering.
    setSelectedDurations({});
    setDayType(extractMatchingDays(orderedSchedule, [...LEGACY_SPECIAL_DAY_TOPICS]));
  };

 const handleInsertModalConfirm = (insertToCurrentDay: boolean) => {
    setShowInsertModal(false);
    
    const dayToUpdate = insertToCurrentDay 
      ? currentDay 
      : `Day ${parseInt(currentDay.split(' ')[1]) + 1}`;
      
          setDayType(prevState => ({
      ...prevState,
      [dayToUpdate]: selectedDayType,
    }));
    
    setSelectedOption("", currentDay);
    
    // The useEffect watching dayType will automatically recalculate the schedule
    // No need for manual recalculation here
  };

  const handleGoClick = (selectedDay: string, selectedOption: string, subjectName: string) => {
    if (isDayRestricted(selectedDay)) {
      alert(`Cannot add day type for Day ${selectedDay.split(' ')[1]} as it is within the restricted range (up to Day ${maxDay})`);
      return;
    }
    if (!selectedOption) return;

    setCurrentDay(selectedDay);
    setCurrentSubjectForTest(subjectName);

    // LCM: mark this learning day (same day — no new day row)
    if (selectedOption === LIVE_SESSION_TOPIC) {
      setSchedule((prev) =>
        applySubjectOrder(
          markDayWithLcm(prev, subjectName, selectedDay, {
            title: 'Live Class Mandatory',
          }),
        ),
      );
      setIncludeLcmFromPlan(true);
      setSelectedOption('', selectedDay);
      return;
    }

    if (selectedOption === 'Test') {
      setShowTestModal(true);
      return;
    }

    setSelectedDayType(selectedOption);
    setShowInsertModal(true);
  };

  const handleIncludeLcmToggle = (include: boolean) => {
    setIncludeLcmFromPlan(include);
    setSchedule((prev) =>
      applySubjectOrder(applyOrClearAllLcm(prev, include)),
    );
  };

  const handleTestModalConfirm = (testTypes: TestKind[], insertToCurrentDay: boolean) => {
    setShowTestModal(false);
    const dayToUpdate = insertToCurrentDay
      ? currentDay
      : `Day ${parseInt(currentDay.split(' ')[1], 10) + 1}`;

    setDayType((prevState) => ({
      ...prevState,
      [dayToUpdate]: { kind: 'test', testTypes },
    }));
    setSelectedOption('', currentDay);
  };

      // Use shared utility functions directly
    const getLastDateOfPreviousSubjectUtil = (currentSubjectName: string): string => {
      return getLastDateOfPreviousSubject(schedule, currentSubjectName, selection.startDate);
    };

  const handleDateEditClick = (subjectName: string) => {
    // Check if the subject's date change is restricted
    if (isSubjectDateRestricted(subjectName)) {
      alert(`Cannot modify dates for subject "${subjectName}" as it has days within the restricted range (up to Day ${maxDay})`);
      return;
    }

    setEditingDateForSubject(subjectName);
  };

  const handleDateChange = (subjectName: string, newDate: string) => {
    if (!newDate) return;

    // Use shared utility directly
    const previousSubjectLastDate = getLastDateOfPreviousSubjectUtil(subjectName);
    const newDateObj = new Date(newDate);
    const previousDate = new Date(previousSubjectLastDate);

    // Allow going back to the original date or any date after the previous subject
    if (newDateObj < previousDate) {
      alert(`Date must be on or after ${previousDate.toLocaleDateString()}. Please select a date from ${previousDate.toLocaleDateString()} onwards`);
      return;
    }

    setCustomSubjectDates(prev => ({
      ...prev,
      [subjectName]: newDate
    }));

    // Use the exact same logic as EditDaywise.tsx
    // First, update the current subject with the new start date
    let updated = updateScheduleWithCustomDate(schedule, subjectName, newDate, selection.saturdayStudyDay, selection.sundayHoliday);
    
    // Then, adjust subsequent subjects if collision occurs (same as EditDaywise.tsx)
    updated = adjustSubsequentSubjects(updated, subjectName, selection.saturdayStudyDay, selection.sundayHoliday);
    
    // Store the new sequenced dates to prevent them from being lost
    const newCustomDates = updateCustomSubjectDatesFromSchedule(updated);
    setCustomSubjectDates(newCustomDates);
    
    // Date edits don't rebuild from fetchedJson; still re-apply order for consistency
    setSchedule(applySubjectOrder(updated));
    setEditingDateForSubject(null);
  };

  // Utility function to clear caches when needed
  const clearCaches = () => {
    clearHolidayCache();
    clearLastDateCache();
  };

  const handleDateEditCancel = () => {
    setEditingDateForSubject(null);
  };

  const handleSubjectReorder = (newOrder: string[]) => {
    subjectOrderRef.current = newOrder;
    selectedNamesRef.current = newOrder;
    setSelectedNames(newOrder);
    const course = Object.keys(fullCourseRef.current).length ? fullCourseRef.current : fetchedJson;
    // Selected subjects only, in user order
    const reorderedCourseData = filterCourseDataByIds(course, subjectNamesToIds(course, newOrder));
    setFetchedJson(reorderedCourseData);

    let reordered = reorderScheduleBySubjectNames(schedule, newOrder);
    reordered = filterScheduleBySelectedNames(reordered, newOrder);

    let anchorIndex = -1;
    for (let i = newOrder.length - 1; i >= 0; i--) {
      if (isSubjectReorderLocked(schedule, newOrder[i], new Date(), maxDay)) {
        anchorIndex = i;
        break;
      }
    }

    if (anchorIndex >= 0 && anchorIndex < newOrder.length - 1) {
      reordered = adjustSubsequentSubjects(
        reordered,
        newOrder[anchorIndex],
        selection.saturdayStudyDay,
        selection.sundayHoliday
      );
    } else if (anchorIndex === -1 && newOrder.length > 1) {
      reordered = adjustSubsequentSubjects(
        reordered,
        newOrder[0],
        selection.saturdayStudyDay,
        selection.sundayHoliday
      );
    }

    // Newly selected: content with empty dates (user sets start date)
    const missing = newOrder.filter((name) => !reordered[name]?.length);
    if (missing.length > 0) {
      const generated = editScheduleData(
        filterCourseDataByIds(course, subjectNamesToIds(course, missing)),
        selection,
        [parseInt(selection.hoursPerDay || '4', 10)]
      );
      missing.forEach((name) => {
        if (!generated[name]) return;
        reordered[name] = generated[name].map((day) => ({
          ...day,
          date: '',
          dayOfWeek: '',
        }));
      });
      reordered = reorderScheduleBySubjectNames(reordered, newOrder);
    }

    if (newOrder.length > 0) {
      reordered = closeScheduleGapsAfterSubject(
        reordered,
        newOrder[0],
        selection.saturdayStudyDay,
        selection.sundayHoliday,
        maxDay
      );
    } else {
      reordered = renumberScheduleDays(reordered, maxDay);
    }

    setCustomSubjectDates(updateCustomSubjectDatesFromSchedule(reordered));
    setSchedule(applySubjectOrder(reordered));
  };

  const handleSelectionChange = (newSelected: string[]) => {
    handleSubjectReorder(newSelected);
  };

  const handleSave = async () => {
    const missingDates = (selectedNamesRef.current.length > 0
      ? selectedNamesRef.current
      : Object.keys(schedule)
    ).filter((name) => {
      const days = schedule[name];
      return !Array.isArray(days) || days.length === 0 || days.some((d) => !d.date);
    });

    if (missingDates.length > 0) {
      alert(
        `Set dates for all selected subjects before saving.\nMissing: ${missingDates.join(', ')}`
      );
      return;
    }

    if (selectedNamesRef.current.length === 0 && Object.keys(schedule).length === 0) {
      alert('Select at least one subject before saving.');
      return;
    }

    // Validate schedule before saving
    if (!validateScheduleDates()) {
      alert('Schedule has invalid dates. Please fix the date conflicts before saving.');
      return;
    }
    
    // Clear caches before saving
    clearCaches();
    
    setShowModal(true);
  };

  const handleModalOk = async () => {
    setIsSaving(true);
    try {
      const course = Object.keys(fullCourseRef.current).length ? fullCourseRef.current : fetchedJson;
      const response = await authService.post('admin/batch/save_daywise/', {
        schedule: applyLiveSessionSubtopicIds(schedule),
        course_id: selection.course_id,
        batch_id: selection.batch_id,
        subject_released: subjectNamesToIds(course, selectedNamesRef.current),
      });
      setShowModal(false);

      if(response.status === 200) {
        setShowUpdateModal(true);
        setModalNewMessage('Schedule updated successfully!');
      }
    } catch (error) {
      console.error('Error sending data:', error);
      setShowModal(false);
      setShowUpdateModal(true);
      setModalNewMessage('Error updating schedule!');
    } finally {
      setIsSaving(false);
    }
  };

  const subjectNames = selectedNames.length > 0 ? selectedNames : extractSubjectNamesFromSchedule(schedule);
  const allSubjectNames = extractSubjectNamesFromCourseData(
    Object.keys(fullCourseRef.current).length ? fullCourseRef.current : fetchedJson
  ).map((s) => s.subjectName);
  const isSubjectLockedForReorder = (name: string) =>
    isSubjectReorderLocked(schedule, name, new Date(), maxDay);
  // Switch to edit drag rules when any subject is locked (e.g. batch already in progress)
  const hasReorderLocks = subjectNames.some(isSubjectLockedForReorder);

  const subjectsMissingDates = subjectNames.filter((name) => {
    const days = schedule[name];
    return !Array.isArray(days) || days.length === 0 || days.some((d) => !d.date);
  });
  const canSave = subjectNames.length > 0 && subjectsMissingDates.length === 0;

  if (!selection.course_id) {
    return (
      <div className="p-4">
        <p className="mb-3">
          Open this page from Batch Calendar. A direct visit has no batch, so the schedule cannot load.
        </p>
        <a className="btn btn-primary" href="/admin/batch-calendar">
          Go to Batch Calendar
        </a>
      </div>
    );
  }

  return (
    <div className="border px-2 rounded-2 bg-white my-2 me-2" style={{ height: `calc(100vh - 90px)`, overflowY: "auto" }}>
      <div>
        <div className="container-fluid mt-2">
          <div className="d-flex justify-content-between align-items-center">
            <div className="flex-grow-1">
              {!canSave && subjectNames.length > 0 && (
                <div className="alert alert-info mb-0 mt-2" role="alert">
                  Set a start date for every selected subject before saving
                  {subjectsMissingDates.length > 0 ? `: ${subjectsMissingDates.join(', ')}` : ''}.
                </div>
              )}
            </div>
            <div className="ms-3 d-flex align-items-center gap-3">
              <div className="form-check mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="include-lcm-from-plan"
                  checked={includeLcmFromPlan}
                  onChange={(e) => handleIncludeLcmToggle(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="include-lcm-from-plan"
                  style={{ fontSize: 13 }}
                >
                  Add live session
                </label>
              </div>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={isSaving || !canSave}
                title={!canSave ? 'Set dates for all selected subjects before saving' : undefined}
              >
                Save
              </button>
            </div>
          </div>
          <div className="container-fluid mt-2">
            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <PulseLoader size={10} />
              </div>
            ) : allSubjectNames.length > 0 || Object.keys(schedule).length > 0 ? (
              <>
                <SubjectReorderPanel
                  mode={hasReorderLocks ? 'edit' : 'create'}
                  subjects={subjectNames}
                  allSubjects={allSubjectNames}
                  isSubjectLocked={hasReorderLocks ? isSubjectLockedForReorder : undefined}
                  canUnselect={(name) => canUnselectSubject(schedule, name, new Date(), maxDay)}
                  onReorder={handleSubjectReorder}
                  onSelectionChange={handleSelectionChange}
                />
                {Object.keys(schedule).length > 0 ? (
                <ScheduleDisplay
                schedule={schedule}
                selection={selection}
                selectedDurations={selectedDurations}
                selectedOptions={selectedOptions}
                editingDateForSubject={editingDateForSubject}
                customSubjectDates={customSubjectDates}
                maxDay={maxDay}
                isDayRestricted={isDayRestricted}
                isSubjectDateRestricted={isSubjectDateRestricted}
                onDurationChange={(day, hours) => {
                  setSelectedDuration(hours, day);
                  handleTimeClick(day, hours);
                }}
                onDayTypeChange={(day, option) => setSelectedOption(option, day)}
                onGoClick={(day, option, subjectName) => {
                  handleGoClick(day, option, subjectName);
                }}
                onDateEditClick={handleDateEditClick}
                onDateChange={handleDateChange}
                onDateEditCancel={handleDateEditCancel}
              />
                ) : (
                  <p className="text-muted mt-3">Select at least one subject to build the schedule.</p>
                )}
              </>
            ) : (
              <p>No schedule data available.</p>
            )}
          </div>
          </div>
        </div>

      
      <Modal show={showModal} onHide={() => !isSaving && setShowModal(false)} centered>
        <Modal.Header closeButton={!isSaving}></Modal.Header>
        <Modal.Body>Are you sure you want to proceed?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleModalOk} disabled={isSaving}>
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Yes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      
      <DayTypeInsertModal
        show={showInsertModal}
        onHide={() => setShowInsertModal(false)}
        onConfirm={handleInsertModalConfirm}
      />

      <TestTypeSelectModal
        show={showTestModal}
        onHide={() => setShowTestModal(false)}
        subjectIndex={extractSubjectNamesFromSchedule(schedule).indexOf(currentSubjectForTest)}
        onConfirm={handleTestModalConfirm}
      />
      
      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Message</Modal.Title>
                </Modal.Header>
                <Modal.Body>{modalNewMessage}</Modal.Body>
            </Modal>
    </div>
  );
};

export default DayWiseGroup;
